/* eslint-disable @typescript-eslint/no-explicit-any */
import { kv } from '@vercel/kv';
import isEmpty from 'lodash/isEmpty';
import type { NextPageContext } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { getCountryStaticProps } from 'src/actions/countryActions';
import {
  searchForAllCountries,
  searchForAllCountriesStaticProps,
} from 'src/actions/searchActions';
import { NavFooter } from 'src/components/nav/navFooter.component';
import { worldDomainName } from 'src/config';
import { countryActions } from 'src/reducers/countryReducer';
import { formatNumber } from 'src/utils/format';
import InformationOutlineIcon from '../assets/InformationOutlineIcon';
import WorldIconFilled from '../assets/WorldIconFilled';
import RecordsStatsChart, {
  fetchChartData,
} from '../components/chart/RecordsStatsChart';
import StandardSEOWrapper from '../components/layout/standardSEOWrapper.component';
import ReactTooltip from '../components/react-tooltip/ReactTooltip';
import RecordedCountriesTable from '../components/table/recorded-countries-table';
import { useRecordedPopulation } from '../hooks/useRecordedPopulation';
import { actions as searchActions } from '../reducers/searchReducer';
import type { DataWorldChartTypes } from '../types/types';
import type { WorldData } from '../types/world';
import { cn } from '../utils/cn';
import { getWorldPopulation } from '../utils/getWorldPopulation';

type WorldProps = {
  WORdata: WorldData;
  countries: DataWorldChartTypes[];
  chartData: Record<
    string,
    { categories: string[]; series: { y: number; created: number }[] }
  >;
};

export default function World({
  countries,
  WORdata,
  chartData,
}: Readonly<WorldProps>) {
  const dispatch = useDispatch();

  const recordedPopulation = useRecordedPopulation({
    lastRun: WORdata?.statistics?.stats_run?.last_run,
    worldLivingPopulation: WORdata?.living,
    worldPendingPopulation: WORdata?.pending,
    loadingText: WORdata ? '' : ' ',
  });

  const worldPopulation = getWorldPopulation();

  const recordedPercentage = Math.min(
    +(
      (Number(recordedPopulation?.replaceAll(',', '')) / worldPopulation) *
      100
    ).toFixed(2),
    99.99
  );

  useEffect(() => {
    dispatch(countryActions.setCountryData({ countryData: WORdata }));
  }, []);

  useEffect(() => {
    if (countries?.length === 0) {
      searchForAllCountries().then((res) => {
        dispatch(searchActions.setAllCountries({ allCountries: res }));
      });
    }
  }, []);

  const { t } = useTranslation(['world']);

  return (
    <div className={`flex min-h-screen flex-col bg-[#FAFAFA]`}>
      <StandardSEOWrapper
        title={t('world:seo.title')}
        description={t('world:seo.description')}
      />
      {!isEmpty(WORdata) ? (
        <div
          className={`flex flex-col px-[20px] max-md:bg-[#FAFAFA] md:px-[45px] tablet:px-[80px]`}
        >
          <div className="flex justify-between pb-6 pt-8 text-gray-950 md:py-[40px]">
            <div className="flex flex-col gap-1 max-md:w-full">
              <div className="flex flex-col gap-3">
                <WorldIconFilled className="rounded-xl" />
                <p className="text-2xl font-semibold xl:text-[32px]">
                  All Countries
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-2xl font-medium tabular-nums text-green-700 xl:text-[32px]">
                  {formatNumber(worldPopulation)}
                </p>
                {recordedPercentage ? (
                  <div className="flex h-9 items-center justify-center rounded-[40px] bg-green-500 px-2.5 py-1">
                    <p className="text-base font-semibold text-white xl:text-lg">
                      {recordedPercentage}%
                    </p>
                  </div>
                ) : null}
              </div>
              <div className="mt-1 flex gap-1 text-xs font-normal leading-none text-zinc-500">
                Total Population
                <InformationOutlineIcon fill="#a1a1aa" height={12} width={12} />
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {!isEmpty(WORdata) && (
        <div className="flex flex-wrap gap-2 px-[20px] max-md:bg-[#FAFAFA] md:gap-5 md:px-[45px] tablet:flex-nowrap tablet:px-[80px]">
          {[
            { title: 'Living', value: recordedPopulation },
            { title: 'Deceased', value: WORdata.dead },
            { title: 'Average Age Years', value: WORdata.average_age },
            {
              title: 'Completion Index',
              value: WORdata.completion_index,
              tooltip:
                'Percentage of information we have on average for each human.',
            },
            {
              title: 'Certainty Index',
              value: WORdata.certainty_index,
              tooltip:
                'The certainty index (1-100) is higher when data is official or corroborated by several sources.',
            },
          ].map((data, index) => (
            <div
              key={index}
              className={cn(
                'inline-flex flex-col items-start justify-start gap-1 rounded-xl bg-white p-6 grow shrink basis-0 shadow-[0px_2px_16px_-4px_rgba(9,9,11,0.08)]',
                index === 0
                  ? 'min-w-full flex-1 tablet:w-fit tablet:min-w-min'
                  : 'w-[calc(50%-10px)] min-w-[calc(50%-10px)] tablet:w-fit tablet:min-w-min'
              )}
            >
              <div className="flex flex-row items-center gap-1 text-sm font-normal leading-tight text-zinc-500">
                <p className="text-xs xl:text-sm">{data.title}</p>
                {data.tooltip ? (
                  <InformationOutlineIcon
                    id={data.title}
                    tooltip={data.tooltip}
                    fill="#a1a1aa"
                    height={12}
                    width={12}
                  />
                ) : null}
              </div>
              <div className="flex flex-col items-start justify-center gap-2">
                <div className="break-all text-xl font-semibold leading-loose text-zinc-950 xl:text-2xl">
                  {formatNumber(
                    !Number.isInteger(data.value) &&
                      typeof data.value === 'number'
                      ? data.value?.toFixed(2)
                      : data.value
                  )}
                </div>
              </div>
              {data.tooltip ? (
                <ReactTooltip
                  className="tooltip-opacity"
                  id={data.title}
                  type={'info'}
                  effect={'solid'}
                  place="top"
                  backgroundColor={'#ECECEC'}
                  textColor={'#454545'}
                />
              ) : null}
            </div>
          ))}
        </div>
      )}
      <RecordsStatsChart chartData={chartData} />

      <RecordedCountriesTable countries={countries} />
      <NavFooter />
    </div>
  );
}

export const getServerSideProps = async ({ locale }: NextPageContext) => {
  let countries;
  let WORdata;
  let chartData;

  const fifteenMinutes = 15 * 60 * 1000; // 15 minutes in milliseconds
  const now = Date.now();

  try {
    // Get cached data and timestamp from KV
    const cachedCountries = await kv.get(`${process.env.KV_ENV}-countries`);
    const cachedWORdata = await kv.get(`${process.env.KV_ENV}-WORdata`);
    const cachedChartData = await kv.get(`${process.env.KV_ENV}-chartData`);

    const cachedTimestamp = await kv.get(
      `${process.env.KV_ENV}-cachedTimestamp`
    );

    // If cache exists and is still valid (not older than 15 minutes), use the cached data
    if (
      cachedCountries &&
      typeof cachedCountries === 'object' &&
      Object.keys(cachedCountries).length > 0 &&
      cachedWORdata &&
      typeof cachedWORdata === 'object' &&
      Object.keys(cachedWORdata).length > 0 &&
      cachedChartData &&
      typeof cachedChartData === 'object' &&
      Object.keys(cachedChartData).length > 0 &&
      now - Number(cachedTimestamp) < fifteenMinutes
    ) {
      countries = cachedCountries;
      WORdata = cachedWORdata;
      chartData = cachedChartData;
    } else {
      // Fetch data from APIs if not present in KV or cache is stale
      [countries, WORdata, chartData] = await Promise.all([
        searchForAllCountriesStaticProps(),
        getCountryStaticProps('WOR', worldDomainName), // Replace with the actual domain name
        fetchChartData(),
      ]);

      // Save fetched data to KV along with the current timestamp
      await Promise.all([
        kv.set(`${process.env.KV_ENV}-countries`, JSON.stringify(countries)),
        kv.set(`${process.env.KV_ENV}-WORdata`, JSON.stringify(WORdata)),
        kv.set(`${process.env.KV_ENV}-chartData`, JSON.stringify(chartData)),
        kv.set(`${process.env.KV_ENV}-cachedTimestamp`, now.toString()), // Save the current timestamp
      ]);
    }
  } catch (error) {
    console.log(error);
  }

  const translations = await serverSideTranslations(locale ?? 'en', [
    'common',
    'about',
    'partners',
    'navbar',
    'new-homepage',
    'new-navbar',
    'new-footer',
    'world',
  ]);
  // Process top first and last names
  let topFirstNames = [],
    topLastNames = [];

  if (Array.isArray(WORdata?.top_first_names)) {
    topFirstNames = WORdata?.top_first_names.slice(0, 100) || [];
  }

  if (Array.isArray(WORdata?.top_last_names)) {
    topLastNames = WORdata?.top_last_names.slice(0, 100) || [];
  }

  let firstNameWithHighestCount: any;
  let lastNamesWithHighestCount: any;

  if (topFirstNames.length > 0) {
    firstNameWithHighestCount = topFirstNames.reduce(
      (maxName: any, currentName: any) => {
        return currentName.count > maxName.count ? currentName : maxName;
      },
      topFirstNames[0]
    );
  }

  if (topLastNames.length > 0) {
    lastNamesWithHighestCount = topLastNames.reduce(
      (maxName: any, currentName: any) => {
        return currentName.count > maxName.count ? currentName : maxName;
      },
      topLastNames[0]
    );
  }

  const firstNamesWithPercentage = topFirstNames.map(
    (currentObject: { name: string; count: number }) => ({
      name: currentObject.name,
      count: currentObject.count,
      percentage: +(
        (currentObject.count / firstNameWithHighestCount?.count) *
        100
      ).toFixed(2),
    })
  );

  const lastNamesWithPercentage = topLastNames.map(
    (currentObject: { name: string; count: number }) => ({
      name: currentObject.name,
      count: currentObject.count,
      percentage: +(
        (currentObject.count / lastNamesWithHighestCount?.count) *
        100
      ).toFixed(2),
    })
  );

  if (WORdata?.top_first_names) {
    WORdata.top_first_names = firstNamesWithPercentage || [];
  }

  if (WORdata?.top_last_names) {
    WORdata.top_last_names = lastNamesWithPercentage || [];
  }

  return {
    props: {
      ...(translations && typeof translations === 'object' ? translations : {}),
      countries: countries ?? [],
      WORdata: WORdata ?? {},
      chartData: chartData ?? {},
    },
  };
};
